import { useSignal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

import {
  canonicalDutchKeysOff,
  DUTCH_DAY_KEYS,
  previewDutchDays,
  type DutchDayKey,
} from '@amumin/domain';
import {
  allDaySets,
  canonical,
  DAY_SETS,
  daySetDef,
  ISLAMIC_TRADITIONS,
  islamicExpected,
  islamicFixable,
  isIslamicTable,
  islamicShiftOptions,
  previewDaySet,
  profileDaysBetween,
  type DaySet,
  type IslamicDayChoice,
  type IslamicTradition,
  type PendingSuggestion,
} from '@amumin/domain/profile';
import { addDays, weekdayIndex, weekdayOf, type LocalDateString } from '@amumin/domain/recurrence';
import { t, type AppMessages, type Messages } from '@amumin/i18n';
import { Button, Checkbox, RadioList } from '@amumin/ui';
import { cx, Icon } from '@amumin/ui-core';

import styles from './profile.module.css';

// Bouwstenen van de profielschermen (docs/DESIGN.md §8.P.2-§8.P.7): chips als role=checkbox in een
// fieldset met legend, schakelaars als role=switch met de setnaam als label, de voorstelkaart als
// region met kop, het toestemmingsblok (nooit voorgevinkt) en de vaste opslagbalk.

export interface ChipOption<T extends string> {
  value: T;
  label: string;
}

export function ChipGroup<T extends string>({
  name,
  legend,
  body,
  hint,
  options,
  selected,
  onToggle,
  headingLevel,
  headingId,
  quietLegend = false,
  srLegend = false,
  children,
}: {
  name: string;
  legend: string;
  body?: string | undefined;
  hint?: string | undefined;
  options: ReadonlyArray<ChipOption<T>>;
  selected: (v: T) => boolean;
  onToggle: (v: T) => void;
  /** Wizard: de legend is ook de schermkop (h1) waar de focus heen gaat. */
  headingLevel?: 'h1' | 'h2' | undefined;
  headingId?: string | undefined;
  /** Instellingen: de blokkop staat erboven, de legend is een gewone vraagregel. */
  quietLegend?: boolean;
  /** Wizard: de schermkop zegt het al; de legend alleen voor schermlezers. */
  srLegend?: boolean;
  children?: ComponentChildren;
}) {
  const Heading = headingLevel ?? null;
  return (
    <fieldset class={styles.fieldset} data-name={name}>
      <legend class={srLegend ? 'fb-sr-only' : quietLegend ? styles.legendQuiet : styles.legend}>
        {Heading ? (
          <Heading id={headingId} tabIndex={-1} style="margin:0;font-size:inherit">
            {legend}
          </Heading>
        ) : (
          legend
        )}
      </legend>
      {body ? <p class={styles.body}>{body}</p> : null}
      {hint ? (
        <p class={styles.hint}>
          <Icon id="info" size={18} />
          <span>{hint}</span>
        </p>
      ) : null}
      <div class={styles.chips}>
        {options.map((o) => {
          const on = selected(o.value);
          return (
            <button
              key={o.value}
              type="button"
              role="checkbox"
              aria-checked={on ? 'true' : 'false'}
              class={styles.chip}
              data-name={`${name}-chip`}
              data-value={o.value}
              onClick={() => onToggle(o.value)}
            >
              {on ? (
                <span class={styles.chipCheck} aria-hidden="true">
                  <Icon id="check" size={18} />
                </span>
              ) : null}
              {o.label}
            </button>
          );
        })}
      </div>
      {children}
    </fieldset>
  );
}

export function Switch({
  checked,
  label,
  onChange,
  name,
}: {
  checked: boolean;
  label: string;
  onChange: (on: boolean) => void;
  name: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked ? 'true' : 'false'}
      aria-label={label}
      class={styles.switch}
      data-name={name}
      onClick={() => onChange(!checked)}
    />
  );
}

/** "Past dit bij jullie? Islam · Halal": platte tekst, nooit voorgevinkt; dagen alleen noemen. */
export function SuggestionCard({
  suggestion,
  a,
  onAccept,
  onDecline,
}: {
  suggestion: PendingSuggestion;
  a: AppMessages;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const p = a.profile;
  const faithNames = p.faiths.labels as Record<string, string>;
  const foodNames = p.food.labels as Record<string, string>;
  const what = [
    ...suggestion.faiths.map((f) => faithNames[f] ?? f),
    ...suggestion.foodRules.map((r) => foodNames[r] ?? r),
  ].join(' · ');
  // Midden in een zin: "Er zijn ook Turkse feestdagen en islamitische feestdagen ...".
  const setNames = p.daySetsInline as Record<string, string>;
  const sets = suggestion.daySets.map((s) => setNames[s] ?? s);
  const setList =
    sets.length <= 1
      ? (sets[0] ?? '')
      : `${sets.slice(0, -1).join(', ')} ${p.warning.and} ${sets[sets.length - 1] ?? ''}`;
  return (
    <section
      class={styles.suggestion}
      role="region"
      aria-labelledby="profile-suggestion-title"
      data-name="profile-suggestion"
    >
      <h2 class={styles.suggestionTitle} id="profile-suggestion-title">
        {p.suggestion.title}
      </h2>
      <p class={styles.suggestionWhat}>{what}</p>
      <p class={styles.suggestionNote}>{p.suggestion.body}</p>
      {sets.length > 0 ? (
        <p class={styles.suggestionNote}>{t(p.suggestion.days, { sets: setList })}</p>
      ) : null}
      <div class={styles.stack}>
        <Button name="profile-suggestion-accept" variant="primary" icon="check" onPress={onAccept}>
          {p.suggestion.accept}
        </Button>
        <Button name="profile-suggestion-decline" onPress={onDecline}>
          {p.suggestion.decline}
        </Button>
      </div>
    </section>
  );
}

export interface DayChoiceDraft {
  daySets: DaySet[];
  dayKeysOff: string[];
}

/** Golf 2: de set "Nederlandse feestdagen" (household.settings.family_layers; geen profiel). */
export interface DutchDaysDraft {
  on: boolean;
  off: DutchDayKey[];
}

/** Golf 2: traditie en bevestigingen voor de islamitische set (0030_snel). */
export interface IslamicDraft {
  /** Keuze van het gezin; null = volgt de keuken. */
  tradition: IslamicTradition | null;
  /** De traditie die nu telt (keuze, anders de standaard bij de keuken). */
  effective: IslamicTradition;
  fixes: Record<string, number>;
}

/** "vr 20 mrt" in de taal van de app (weekdagen beginnen op maandag). */
function shortDate(date: LocalDateString, m: Messages): string {
  const [, mo, day] = date.split('-').map(Number) as [number, number, number];
  const wd = weekdayIndex(weekdayOf(date));
  return `${m.weekdaysShort[wd] ?? ''} ${day} ${m.monthsShort[mo - 1] ?? ''}`;
}

/** "vr 20 of za 21 mrt" (zelfde maand) of "di 31 mrt of wo 1 apr". */
export function eitherOrLabel(
  date: LocalDateString,
  alternative: LocalDateString | null,
  a: AppMessages,
  m: Messages,
): string {
  if (!alternative) return shortDate(date, m);
  const sameMonth = date.slice(0, 7) === alternative.slice(0, 7);
  const first = sameMonth
    ? `${m.weekdaysShort[weekdayIndex(weekdayOf(date))] ?? ''} ${Number(date.slice(8, 10))}`
    : shortDate(date, m);
  return t(a.profile.days.eitherOr, { a: first, b: shortDate(alternative, m) });
}

/** Naam van een bijzondere dag in de taal van de app (Engels: "Eid al-Fitr"). */
export function dayName(key: string, fallback: string, a: AppMessages): string {
  return a.profile.days.dayNames[key] ?? fallback;
}

/** "Eerstvolgende: 29 okt, 23 apr, 19 mei (verwacht)" voor een set (ook als hij uit staat). */
function previewLine(
  set: DaySet,
  today: LocalDateString,
  a: AppMessages,
  m: Messages,
  islamic: IslamicDayChoice | undefined,
): string {
  const p = a.profile.days;
  // Elke dag één keer (Keti Koti niet twee jaar achter elkaar).
  const seen: string[] = [];
  const days = previewDaySet(set, today, 6, islamic).filter((d) => {
    if (seen.indexOf(d.key) >= 0) return false;
    seen.push(d.key);
    return true;
  });
  if (days.length === 0) return p.noDates;
  const culture = daySetDef(set).kind === 'culture_day';
  const dates = days
    .slice(0, 3)
    .map((d) => {
      const [, mo, day] = d.date.split('-').map(Number) as [number, number, number];
      const short = `${day} ${m.monthsShort[mo - 1] ?? ''}`;
      // Culturele dagen zonder de uitleg tussen haakjes ("19 Mayıs"); geloofsdagen voluit
      // ("Eid al-Fitr (Suikerfeest)", golf 2).
      const full = dayName(d.key, d.name, a);
      const name = culture ? full.replace(/\s*\([^)]*\)$/, '') : full;
      if (d.alternative) {
        return `${name} ${eitherOrLabel(d.date, d.alternative, a, m)} ${p.expected}`;
      }
      // Is de naam zelf al de datum ("23 Nisan", "19 Mayıs"), dan niet nog eens "23 apr".
      const when = name.indexOf(`${day} `) === 0 ? '' : ` ${short}`;
      return `${name}${when}${d.expected ? ` ${p.expected}` : ''}`;
    })
    .join(', ');
  return t(p.next, { dates });
}

/** Voorvertoning van de Nederlandse feestdagen ("Eerstvolgende: Sinterklaas 5 dec, ..."). */
function dutchPreviewLine(
  today: LocalDateString,
  off: ReadonlyArray<string>,
  a: AppMessages,
  m: Messages,
): string {
  const p = a.profile.days;
  const names = p.nl.days as Record<string, string>;
  const days = previewDutchDays(today, 3, off);
  if (days.length === 0) return p.noDates;
  return t(p.next, {
    dates: days
      .map((d) => {
        const [, mo, day] = d.date.split('-').map(Number) as [number, number, number];
        return `${names[d.key] ?? d.name} ${day} ${m.monthsShort[mo - 1] ?? ''}`;
      })
      .join(', '),
  });
}

/**
 * De eerste rij: "Nederlandse feestdagen", standaard AAN (golf 2, oprichter #2). Per set of per dag
 * uit te zetten, zoals de andere sets; geen toestemming nodig (geen gevoelig gegeven).
 */
function DutchDaysRow({
  value,
  onChange,
  today,
  a,
  m,
}: {
  value: DutchDaysDraft;
  onChange: (v: DutchDaysDraft) => void;
  today: LocalDateString;
  a: AppMessages;
  m: Messages;
}) {
  const p = a.profile.days;
  const open = useSignal(false);
  const names = p.nl.days as Record<string, string>;
  const dayOn = (key: DutchDayKey, on: boolean) => {
    let off = on ? value.off.filter((k) => k !== key) : [...value.off, key];
    let setOn = value.on;
    if (on && !value.on) {
      // Eén dag aan in een set die uit staat: de set aan, de andere dagen uit.
      setOn = true;
      off = DUTCH_DAY_KEYS.filter((k) => k !== key);
    }
    if (DUTCH_DAY_KEYS.every((k) => off.indexOf(k) >= 0)) {
      setOn = false;
      off = [];
    }
    onChange({ on: setOn, off: canonicalDutchKeysOff(off) });
  };
  return (
    <li class={styles.dayRow} data-name="profile-dayset" data-set="nl_public">
      <div class={styles.dayHead}>
        <span class={styles.dayText}>
          <span class={styles.dayName} id="dayset-nl_public">
            {p.nl.title}
          </span>
          <span class={styles.dayPreview}>{dutchPreviewLine(today, value.off, a, m)}</span>
          <span class={styles.dayPreview}>{p.nl.note}</span>
        </span>
        <Switch
          checked={value.on}
          label={p.nl.title}
          onChange={(v) => onChange({ on: v, off: [] })}
          name="profile-dayset-switch"
        />
      </div>
      <button
        type="button"
        class={styles.linkButton}
        aria-expanded={open.value ? 'true' : 'false'}
        onClick={() => (open.value = !open.value)}
      >
        {p.perDay}
      </button>
      {open.value ? (
        <ul class={styles.perDay}>
          {DUTCH_DAY_KEYS.map((k) => (
            <li key={k} class={styles.perDayRow}>
              <span class={styles.perDayName}>{names[k] ?? k}</span>
              <Switch
                checked={value.on && value.off.indexOf(k) < 0}
                label={names[k] ?? k}
                onChange={(v) => dayOn(k, v)}
                name="profile-day-switch"
              />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Zo ver vooruit zijn islamitische dagen te bevestigen (Bijzondere dagen toont ze allemaal; de
 * agenda biedt "Dag bevestigen" alleen binnen dit venster).
 */
export const ISLAMIC_CONFIRM_DAYS = 400;

/**
 * Welke kalender volgt het gezin voor de islamitische dagen (golf 2, oprichter #1), met daaronder
 * de eerstvolgende dagen die nog niet vast staan: een ouder tikt de dag die de moskee koos.
 */
function IslamicTraditionBlock({
  value,
  onChange,
  today,
  a,
  m,
}: {
  value: IslamicDraft;
  onChange: (v: IslamicDraft) => void;
  today: LocalDateString;
  a: AppMessages;
  m: Messages;
}) {
  const tr = a.profile.days.tradition;
  const t0 = value.effective;
  const choice: IslamicDayChoice = { tradition: t0, fixes: value.fixes };
  // Alle eerstvolgende hoofddagen (dag 1) binnen ruim een jaar (review g2 r2m: niet hooguit vier;
  // elke dag waarbij de agenda "Dag bevestigen" biedt, moet hier te kiezen zijn).
  const upcoming = islamicFixable(t0)
    ? profileDaysBetween(today, addDays(today, ISLAMIC_CONFIRM_DAYS), {
        daySets: ['islamic'],
        dayKeysOff: [],
        islamic: choice,
      })
        // Alleen de hoofddagen zelf: "Laatste dag Ramadan" hoort bij Eid al-Fitr (zelfde sleutel).
        .filter((d) => d.dayIndex === 1 && d.fixKey !== null && isIslamicTable(d.key))
    : [];
  const setFix = (key: string, shift: number | null) => {
    const fixes = { ...value.fixes };
    if (shift === null) delete fixes[key];
    else fixes[key] = shift;
    onChange({ ...value, fixes });
  };
  return (
    <div class={styles.tradition} data-name="profile-islamic-tradition">
      <RadioList<IslamicTradition>
        name="islamic-tradition"
        label={tr.title}
        value={t0}
        onChange={(v) =>
          // Andere traditie: de bevestigingen horen bij de oude lijn en vervallen.
          onChange({ tradition: v, effective: v, fixes: v === t0 ? value.fixes : {} })
        }
        options={ISLAMIC_TRADITIONS.map((x) => ({
          value: x,
          label: tr.options[x],
          ...(x === t0 ? { hint: tr.hints[x] } : {}),
        }))}
      />
      {value.tradition === null ? <p class={styles.shows}>{tr.defaultNote}</p> : null}
      {upcoming.length > 0 ? (
        <div class={styles.confirm} data-name="profile-islamic-confirm">
          <p class={styles.subTitle}>{tr.confirmTitle}</p>
          <p class={styles.shows}>{tr.confirmBody}</p>
          <ul class={styles.confirmList}>
            {upcoming.map((d) => {
              const key = d.fixKey ?? '';
              const expectedDate = key.slice(key.indexOf(':') + 1) as LocalDateString;
              const fix = value.fixes[key];
              const name = dayName(d.key, d.name, a);
              return (
                <li key={key} class={styles.confirmRow} data-name="profile-islamic-day">
                  <span class={styles.confirmName}>
                    {name}
                    <span class={styles.dayPreview}>
                      {typeof fix === 'number'
                        ? `${shortDate(d.date, m)} · ${tr.confirmedNote}`
                        : `${eitherOrLabel(d.date, d.alternative, a, m)} ${a.profile.days.expected}`}
                    </span>
                  </span>
                  <span class={styles.confirmChips} role="group" aria-label={name}>
                    {islamicShiftOptions(t0).map((shift) => {
                      const date = addDays(expectedDate, shift);
                      const on = fix === shift;
                      return (
                        <button
                          key={shift}
                          type="button"
                          class={styles.chip}
                          aria-pressed={on ? 'true' : 'false'}
                          data-name="profile-islamic-fix"
                          onClick={() => setFix(key, on ? null : shift)}
                        >
                          {shortDate(date, m)}
                        </button>
                      );
                    })}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Bijzondere dagen in de agenda (§8.P.7): eerst "Nederlandse feestdagen" (standaard AAN, golf 2),
 * dan de aangeboden sets (UIT), dan onder "Meer bijzondere dagen" de rest. Een set gaat met één
 * tik aan; "Kies per dag" zet losse dagen uit. Bij de islamitische set (aan) de vraag welke
 * kalender het gezin volgt.
 */
export function DaySetList({
  offered,
  value,
  onChange,
  today,
  a,
  m,
  dutch,
  onDutch,
  islamic,
  onIslamic,
}: {
  offered: ReadonlyArray<DaySet>;
  value: DayChoiceDraft;
  onChange: (v: DayChoiceDraft) => void;
  today: LocalDateString;
  a: AppMessages;
  m: Messages;
  /** Golf 2: de Nederlandse feestdagen als eerste set (weg = niet tonen). */
  dutch?: DutchDaysDraft | undefined;
  onDutch?: ((v: DutchDaysDraft) => void) | undefined;
  /** Golf 2: traditie voor de islamitische set. */
  islamic?: IslamicDraft | undefined;
  onIslamic?: ((v: IslamicDraft) => void) | undefined;
}) {
  const p = a.profile.days;
  const showMore = useSignal(false);
  const open = useSignal<ReadonlyArray<string>>([]);
  const setNames = p.sets as Record<string, string>;
  // Aangeboden sets en sets die al aan staan altijd zichtbaar; de rest onder "Meer".
  const first = canonical([...offered, ...value.daySets], DAY_SETS).sort(
    (x, y) =>
      (offered.indexOf(x) < 0 ? 99 : offered.indexOf(x)) -
      (offered.indexOf(y) < 0 ? 99 : offered.indexOf(y)),
  );
  const rest = allDaySets().filter((s) => first.indexOf(s) < 0);
  const visible = showMore.value ? [...first, ...rest] : first;
  const islamicChoice: IslamicDayChoice | undefined = islamic
    ? { tradition: islamic.effective, fixes: islamic.fixes }
    : undefined;

  const setOn = (s: DaySet, on: boolean) => {
    const daySets = on
      ? canonical([...value.daySets, s], DAY_SETS)
      : value.daySets.filter((x) => x !== s);
    const keys = daySetDef(s).days.map((d) => d.key);
    // Een set aan of uit zet ook zijn losse dagen terug op "aan" (geen verborgen restant).
    const dayKeysOff = value.dayKeysOff.filter((k) => keys.indexOf(k) < 0);
    onChange({ daySets, dayKeysOff });
  };

  const dayOn = (s: DaySet, key: string, on: boolean) => {
    let daySets = value.daySets;
    let off = on ? value.dayKeysOff.filter((k) => k !== key) : [...value.dayKeysOff, key];
    if (on && daySets.indexOf(s) < 0) {
      // Eén dag aanzetten in een set die uit staat: de set aan, de andere dagen uit.
      daySets = canonical([...daySets, s], DAY_SETS);
      off = [
        ...off,
        ...daySetDef(s)
          .days.map((d) => d.key)
          .filter((k) => k !== key && off.indexOf(k) < 0),
      ];
    }
    const keys = daySetDef(s).days.map((d) => d.key);
    if (keys.every((k) => off.indexOf(k) >= 0)) {
      // Alle dagen uit = de set uit.
      daySets = daySets.filter((x) => x !== s);
      off = off.filter((k) => keys.indexOf(k) < 0);
    }
    onChange({ daySets, dayKeysOff: off });
  };

  return (
    <div data-name="profile-days">
      <ul class={styles.dayList}>
        {dutch && onDutch ? (
          <DutchDaysRow value={dutch} onChange={onDutch} today={today} a={a} m={m} />
        ) : null}
        {visible.map((s) => {
          const def = daySetDef(s);
          const on = value.daySets.indexOf(s) >= 0;
          const expanded = open.value.indexOf(s) >= 0;
          const expected =
            s === 'islamic'
              ? islamicExpected(islamic?.effective ?? 'diyanet')
              : def.days.some((d) => d.rule.kind === 'table');
          const eve = def.days.some((d) => d.eveBefore === true);
          return (
            <li key={s} class={styles.dayRow} data-name="profile-dayset" data-set={s}>
              <div class={styles.dayHead}>
                <span class={styles.dayText}>
                  <span class={styles.dayName} id={`dayset-${s}`}>
                    {setNames[s] ?? s}
                  </span>
                  <span class={styles.dayPreview}>
                    {previewLine(s, today, a, m, s === 'islamic' ? islamicChoice : undefined)}
                  </span>
                </span>
                <Switch
                  checked={on}
                  label={setNames[s] ?? s}
                  onChange={(v) => setOn(s, v)}
                  name="profile-dayset-switch"
                />
              </div>
              {s === 'islamic' && on && islamic && onIslamic ? (
                <IslamicTraditionBlock
                  value={islamic}
                  onChange={onIslamic}
                  today={today}
                  a={a}
                  m={m}
                />
              ) : null}
              <button
                type="button"
                class={styles.linkButton}
                aria-expanded={expanded ? 'true' : 'false'}
                onClick={() =>
                  (open.value = expanded ? open.value.filter((x) => x !== s) : [...open.value, s])
                }
              >
                {p.perDay}
              </button>
              {expanded ? (
                <>
                  <ul class={styles.perDay}>
                    {def.days.map((d) => {
                      const dayIsOn = on && value.dayKeysOff.indexOf(d.key) < 0;
                      const name = dayName(d.key, d.name, a);
                      return (
                        <li key={d.key} class={styles.perDayRow}>
                          <span class={styles.perDayName}>{name}</span>
                          <Switch
                            checked={dayIsOn}
                            label={name}
                            onChange={(v) => dayOn(s, d.key, v)}
                            name="profile-day-switch"
                          />
                        </li>
                      );
                    })}
                  </ul>
                  {expected ? <p class={styles.shows}>{p.expectedNote}</p> : null}
                  {eve ? <p class={styles.shows}>{p.eveBefore}</p> : null}
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
      {visible.length === 0 && !dutch ? (
        <p class={styles.body}>{a.profile.screens.offeredEmpty}</p>
      ) : null}
      {rest.length > 0 ? (
        <button
          type="button"
          class={styles.linkButton}
          aria-expanded={showMore.value ? 'true' : 'false'}
          data-name="profile-days-more"
          onClick={() => (showMore.value = !showMore.value)}
        >
          {showMore.value ? a.profile.screens.lessDays : p.more}
        </button>
      ) : null}
    </div>
  );
}

/** Waar de privacyverklaring staat (openbaar, ook zonder sessie). */
export const PRIVACY_PATH = '/privacy';

/** Link naar de privacyverklaring; opent in een nieuw tabblad zodat een half ingevulde keuze blijft. */
export function PrivacyLink({ a }: { a: AppMessages }) {
  return (
    <a
      href={PRIVACY_PATH}
      target="_blank"
      rel="noopener"
      class={styles.privacyLink}
      data-name="profile-privacy-link"
    >
      {a.profile.privacyLink}
    </a>
  );
}

/**
 * "Mag dit?": de toestemmingstekst (versie + hash) en het vinkje, nooit voorgevinkt. De tekst van
 * het gezin staat in korte punten (één per regel in i18n, COGA), met de privacyverklaring erbij.
 */
export function ConsentBlock({
  checked,
  onChange,
  renew,
  a,
  self = false,
}: {
  checked: boolean;
  onChange: (on: boolean) => void;
  renew?: boolean;
  a: AppMessages;
  /** Eigen toestemming van een volwassene (selfConsent) in plaats van die van het gezin. */
  self?: boolean;
}) {
  const c = self ? a.profile.selfConsent : a.profile.consent;
  return (
    <section
      class={styles.consent}
      aria-labelledby="profile-consent-title"
      data-name="profile-consent"
    >
      <h2 class={styles.consentTitle} id="profile-consent-title">
        {self ? a.profile.selfConsent.title : a.profile.consent.title}
      </h2>
      {renew ? <p class={styles.consentText}>{a.profile.consent.renew}</p> : null}
      {self ? (
        <p class={styles.consentText}>{c.text}</p>
      ) : (
        <ul class={styles.consentList}>
          {c.text.split('\n').map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ul>
      )}
      <p class={styles.consentText}>
        <PrivacyLink a={a} />
      </p>
      <Checkbox
        id={self ? 'profile-self-consent' : 'profile-consent-check'}
        label={c.check}
        checked={checked}
        onChange={onChange}
      />
    </section>
  );
}

/** Vaste opslagbalk onderaan (op de telefoon boven de tabbalk als `aboveTabs`). */
export function SaveBar({
  children,
  note,
  aboveTabs = false,
}: {
  children: ComponentChildren;
  note?: string | undefined;
  aboveTabs?: boolean;
}) {
  // Meldingen (toasts) staan boven de balk, nooit over Opslaan (review golf 6b founder-ux r1):
  // de balk meldt zijn bovenrand als --fixed-actions-h (Toast.module.css).
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = bar.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const measure = () => {
      const top = el.getBoundingClientRect().top;
      root.style.setProperty('--fixed-actions-h', `${Math.max(0, window.innerHeight - top)}px`);
    };
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', measure);
      root.style.removeProperty('--fixed-actions-h');
    };
  }, []);
  return (
    <>
      <div class={styles.saveSpacer} aria-hidden="true" />
      <div
        ref={bar}
        class={cx(styles.saveBar, aboveTabs && styles.saveBarAboveTabs)}
        data-name="profile-save-bar"
      >
        <div class={styles.saveBarInner}>
          {note ? <p class={styles.saveBarNote}>{note}</p> : null}
          {children}
        </div>
      </div>
    </>
  );
}
